import React, { createContext, FC, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cloneDeep from "lodash/cloneDeep";
import cn from "clsx";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { changeSelectedShop } from "@/redux/actions/storeLocator.action";
import { getCountryList } from "@/redux/actions/common.action";
import { getStoreLocatorList } from "@/redux/actions/storeLocator.action";
import Page from "@/components/page";
import GoogleMap from "@/components/google/googleMap";
import StoreLocatorDetails from "@/components/storeLocator/storeLocatorDetails";
import StoreLocatorThumbSwiper from "@/components/storeLocator/storeLocatorThumbSwiper";
import StoreLocatorHeader from "@/components/storeLocator/storeLocatorHeader";
import { GOOGLE_MAPS_APIS_SRC } from "@/constants";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import { withPageInitialLoad } from "@/libs/pageDataBridge";
import { addScriptToBody } from "@/util/dom";
import { isServer } from "../../util";
import { useIsRTL } from "@/hooks";
import "./style.scss";

type LocationProps = {
  shopItem: { lat: number; lng: number };
  zoom?: number;
};

const StoreLocator = () => {
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [mapVisible, setMapVisible] = useState(false);
  const [locationData, setLocationData] = useState<{
    lat: number;
    lng: number;
    zoom: number;
  }>({
    lat: 0,
    lng: 0,
    zoom: 0
  });

  const dispatch = useAppDispatch();
  const isRTL = useIsRTL();

  const { t } = useTranslation("storeLocator");
  const { t: seoT } = useTranslation("seo", { lng: "en" });

  const selectedCountry = useAppSelector(
    state => state.storeLocatorReducer.selectedCountry
  );
  const selectedShop = useAppSelector(
    state => state.storeLocatorReducer.selectedShop
  );
  const countryCode = useAppSelector(
    state => state.common.settings.countryCode
  );
  const locatorList = useAppSelector(
    state => state.storeLocatorReducer.locatorList
  );

  useEffect(() => {
    if (!isServer) {
      addScriptToBody(GOOGLE_MAPS_APIS_SRC);
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        success => {
          setLatitude(success.coords.latitude);
          setLongitude(success.coords.longitude);
        },
        failure => {
          console.log(failure.message);
        }
      );
    } else {
      console.log("Geolocation is not supported by this browser.");
    }
  }, []);

  const handleCurrentLocation = () => {
    setMapVisible(true);
    setLocationData({ lat: latitude || 0, lng: longitude || 0, zoom: 16 });

    CommonAnalyticsEvent.nearByStore({
      shop: selectedShop
    });
    CommonAnalyticsEvent.clickToAction({
      label: t("useMyCurrentLocation"),
      type: "button"
    });
  };

  const updatedStoreList = () => {
    let store = [];
    countryCode &&
      locatorList &&
      locatorList.filter(item => {
        if (
          item.countryId === countryCode ||
          item.country === selectedCountry
        ) {
          const copiedItem = cloneDeep(item);
          store = [...copiedItem.shops];
        }
        return null;
      });

    !!store.length &&
      store.forEach(item => {
        if (item.id) {
          item["station_id"] = item.id;
        }
      });
    return store;
  };

  const mapContainerHeight =
    !isServer && document.body.clientWidth > 813 ? "350px" : "225px";

  const handleChangeSelectedShop = ({ shopItem, zoom = 0 }: LocationProps) => {
    setLocationData({ lat: shopItem.lat, lng: shopItem.lng, zoom });

    dispatch(changeSelectedShop(shopItem));
  };

  return (
    <Page
      title={`${seoT("ourStores")} | ${seoT("bfl")}`}
      description={seoT("storeLocatorMeta")}
      id="store-locator"
      indexable
    >
      <div
        className={cn("store-locator-container", {
          arabic: isRTL
        })}
      >
        <StoreLocatorContext.Provider
          value={{
            handleCurrentLocation,
            selectedCountry,
            selectedShop,
            handleChangeSelectedShop
          }}
        >
          <StoreLocatorHeader />

          {!!selectedShop && Object.keys(selectedShop).length ? (
            <div className="container padding-web-0 store-locator-body">
              <div className="store_preview">
                {!mapVisible && (
                  <StoreLocatorThumbSwiper selectedShop={selectedShop} />
                )}
                {mapVisible && (
                  <div className="map_container">
                    <GoogleMap
                      stores={updatedStoreList()}
                      selectedStore={selectedShop}
                      lat={+locationData.lat}
                      lng={+locationData.lng}
                      zoom={+locationData.zoom}
                      mapContainerHeight={mapContainerHeight}
                    />
                  </div>
                )}
              </div>
              <div className="store_detail">
                <StoreLocatorDetails mapVisible={mapVisible} />
              </div>
            </div>
          ) : (
            <div className="noStoresFound">
              {" "}
              <span>{t("noStoresFound")}</span>
            </div>
          )}
        </StoreLocatorContext.Provider>
      </div>
    </Page>
  );
};

export const StoreLocatorContext = createContext<{
  handleCurrentLocation: () => void;
  selectedCountry: any;
  selectedShop: any;
  handleChangeSelectedShop: (val: LocationProps) => void;
}>(null);

export default withPageInitialLoad({
  pageName: "store-locator",
  page: withRouter(StoreLocator),
  strategy: async ({ store, language }) => {
    const { common } = store.getState();
    const { settings } = common;
    await store.dispatch(getCountryList(language));
    await store.dispatch(getStoreLocatorList(language, settings.countryId));
  },
  ssr: true,
  loader: false
});
